import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {reduxToggleSidePanel} from 'appredux/actions';
import {ESidePanelView} from 'constants/index';
import {
  ICustomerSupplier,
  IDepot,
  IFarmInputProduct,
  IFdApplicationType,
  IFdCrop,
  IFdFarm,
  IFdFarmAssetType,
  IFdFinancialPerformance,
  IFdLiability,
  IFdOperatingCost,
  IFdProductionCost,
  IFdProductionHistory,
  IFdProductionPlan,
  IFdUser,
  ISidePanel,
  ISupplierCompanyProfile,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {IconButton, Typography} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {PermissionsService} from 'services/PermissionsService';
import {ProductState} from 'appredux/reducers/reducer-products';
import {DepotState} from 'appredux/reducers/reducer-depots';

// Panel sub-components
import Invoice from './Panels/Invoice/Invoice';
import RemoveTeamMember from 'screens/Dashboard/utils/SidePanel/Panels/Team/RemoveTeamMember';
import AddQuote from 'screens/Dashboard/utils/SidePanel/Panels/Quotes/AddQuote';
import AddCustomer from 'screens/Dashboard/utils/SidePanel/Panels/Customers/AddCustomer';
import AddOrUpdateDepot from 'screens/Dashboard/utils/SidePanel/Panels/Depots/AddOrUpdateDepot';
import AddOrUpdateProduct from 'screens/Dashboard/utils/SidePanel/Panels/Products/AddOrUpdateProduct';
import PromoteProduct from 'screens/Dashboard/utils/SidePanel/Panels/Products/PromoteProduct';
import AddOrUpdateTeamMember from 'screens/Dashboard/utils/SidePanel/Panels/Team/AddOrUpdateTeamMember';
import EditTeamMemberPermissions from 'screens/Dashboard/utils/SidePanel/Panels/Team/EditTeamMemberPermissions';
import AddOrUpdateInventory from 'screens/Dashboard/utils/SidePanel/Panels/Products/AddOrUpdateInventory';
import AddOrUpdateProgram from 'screens/Dashboard/utils/SidePanel/Panels/Programs/AddOrUpdateProgram';
import AddOrUpdateProgramLine from 'screens/Dashboard/utils/SidePanel/Panels/Programs/AddOrUpdateProgramLine';
import AddOrUpdateSoilData from 'screens/Dashboard/utils/SidePanel/Panels/SoilData/AddOrUpdateSoilData';
import AddContracts from './Panels/Contracts/AddContracts';
import AddOrUpdateFdUser from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdUser';
import AddOrUpdateFdFarm from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdFarm';
import AddOrUpdateFdCompany from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdCompany';
import EditFdProductionPlans from 'screens/Dashboard/utils/SidePanel/Panels/Applications/EditFdProductionPlans';
// eslint-disable-next-line max-len
import AddOrUpdateFdProductionPlan from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdProductionPlan';
// eslint-disable-next-line max-len
import AddOrUpdateFdProductionHistory from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdProductionHistory';
// eslint-disable-next-line max-len
import AddOrUpdateFdFinancialHistory from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdFinancialHistory';
import EditFdOperatingCosts from 'screens/Dashboard/utils/SidePanel/Panels/Applications/EditFdOperatingCosts';
// eslint-disable-next-line max-len
import AddOrUpdateFdOperatingCost from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdOperatingCost';
// eslint-disable-next-line max-len
import AddOrUpdateWalletRestrictions from 'screens/Dashboard/utils/SidePanel/Panels/Wallets/AddOrUpdateWalletRestrictions';
import EditFdAssets from 'screens/Dashboard/utils/SidePanel/Panels/Applications/EditFdAssets';
import AddOrUpdateFdAsset from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdAsset';
import AddOrUpdateFdLiability from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdLiability';
import EditFdLiabilities from 'screens/Dashboard/utils/SidePanel/Panels/Applications/EditFdLiabilities';
// eslint-disable-next-line max-len
import AddOrUpdateFdProductionCost from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdProductionCost';
import AddOrUpdateFdTeam from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdTeam';
import AddOrUpdateFdCustomers from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdCustomers';
import AddOrUpdateFdSuppliers from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdSuppliers';
import AddOrUpdateFdRisks from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdRisks';
import AddOrUpdateFdFundingAsk from 'screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdFundingAsk';
import CustomizeOverviewPanel from 'screens/Dashboard/utils/SidePanel/Panels/Overview/CustomizeOverviewPanel';

/**
 * Example shape for passing "inventory" data to the side panel
 */

export type InventorySidePanelData = {
  product: IFarmInputProduct;
  depotIds: string[];
};

export type ProductionPlanAndHistorySidePanelData = {
  applicationId: number;
  farmId: number;
  plans: IFdProductionPlan[];
  history: IFdProductionHistory[];
};

export type ProductionPlanSidePanelData = {
  applicationId: number;
  plan: IFdProductionPlan;
};

export type ProductionHistorySidePanelData = {
  applicationId: number;
  farmId: number;
  history: IFdProductionHistory[];
  cropId: string;
  cropName: string;
};

export type FinancialHistorySidePanelData = {
  applicationId: number;
  companyId: number;
  history: IFdFinancialPerformance[];
  application: IFdApplicationType; // Add application to access operating costs
};

export type OperatingCostsSidePanelData = {
  applicationId: number;
  operatingCosts: IFdOperatingCost[];
};

export type OperatingCostSidePanelData = {
  applicationId: number;
  operatingCost?: IFdOperatingCost;
};

export type AssetsSidePanelData = {
  applicationId: number;
  farmId: number;
  assets: IFdFarmAssetType[];
  onAssetUpserted?: () => void;
};

export type AssetSidePanelData = {
  applicationId: number;
  farmId: number;
  asset?: IFdFarmAssetType;
  onAssetUpserted?: () => void;
};

export type LiabilitiesSidePanelData = {
  applicationId: number;
  liabilities: IFdLiability[];
};

export type LiabilitySidePanelData = {
  applicationId: number;
  liability?: IFdLiability;
};

export type ProductionCostSidePanelData = {
  applicationId: number;
  productionCost: IFdProductionCost;
  crop: IFdCrop;
};

export type CustomerSupplierSidePanelData = {
  applicationId: number; // FD application ID
  customerSuppliers: ICustomerSupplier[]; // existing pairs
};

export type FarmerSupplierSidePanelData = {
  applicationId: number;
  farmerSuppliers: any[]; // GraphQL rows as above
};

export type RisksSidePanelData = {
  applicationId: number;
};

type SidePanelProps = {
  sidepanel?: ISidePanel;
  user: ISupplierUserProfile;
  productState: ProductState;
  depotState: DepotState;
  children?: any;
};

export type FundingAskSidePanelData = {
  applicationId: number;
  application: IFdApplicationType;
};

const SidePanel = (props: SidePanelProps) => {
  const [opacityVisible, setOpacityVisible] = useState(false);
  const [sidepanel, setSidepanel] = useState<ISidePanel | undefined>(
    props.sidepanel,
  );

  // Close handler
  const closePanel = () => reduxToggleSidePanel(false, null);

  // Sync local state when props.sidepanel changes
  useEffect(() => {
    setSidepanel(props.sidepanel);
    if (props.sidepanel?.open && !props.sidepanel.withoutOverlay) {
      setTimeout(() => setOpacityVisible(true), 300);
    } else {
      setOpacityVisible(false);
    }
  }, [props.sidepanel]);

  const isOpen = sidepanel && sidepanel.open;
  const sidePanelData = sidepanel?.panelInfo?.data;
  const panelType = sidepanel?.panelInfo?.panelType;
  const panelInfo = sidepanel?.panelInfo;

  // Example: for a "wide" panel if creating a quote
  const isWide = panelType === ESidePanelView.CREATE_QUOTE;

  // Permissions-based logic
  const depots = new PermissionsService(props.user).getEditableDepots(
    props.depotState.depots,
  );

  // Dynamically compute the panel's title
  function panelTitle() {
    switch (panelType) {
      case ESidePanelView.INVOICE:
        return 'Invoice';
      case ESidePanelView.ADD_TEAM_MEMBER:
        return 'Add Team Member';
      case ESidePanelView.EDIT_TEAM_MEMBER: {
        const teamMember = sidePanelData as ISupplierUserProfile;
        return `${teamMember.name} ${teamMember.surname}`;
      }
      case ESidePanelView.EDIT_TEAM_MEMBER_PERMISSIONS: {
        const teamMember = sidePanelData as ISupplierUserProfile;
        return `${teamMember.name} ${teamMember.surname} Permissions`;
      }
      case ESidePanelView.REMOVE_TEAM_MEMBER: {
        const teamMember = sidePanelData as ISupplierUserProfile;
        return `Remove ${teamMember.name} ${teamMember.surname}`;
      }
      case ESidePanelView.CREATE_QUOTE:
        return 'Create Quote';
      case ESidePanelView.ADD_CUSTOMER:
        return 'Add Customer';
      case ESidePanelView.ADD_DEPOT:
        return 'Add Depot';
      case ESidePanelView.EDIT_DEPOT: {
        const depot = sidePanelData as IDepot;
        return depot.name;
      }
      case ESidePanelView.ADD_PRODUCT:
        return 'Add Product';
      case ESidePanelView.EDIT_PRODUCT: {
        const product = sidePanelData as IFarmInputProduct;
        return product.name;
      }
      case ESidePanelView.COPY_PRODUCT:
        return 'Add Product';
      case ESidePanelView.PROMOTE_PRODUCT: {
        const product = sidePanelData as IFarmInputProduct;
        return `Promote ${product.name}`;
      }
      case ESidePanelView.EDIT_INVENTORY: {
        const productData = sidePanelData as InventorySidePanelData;
        return productData.product?.name;
      }
      case ESidePanelView.ADD_PROGRAM:
        return 'Add Program';
      case ESidePanelView.ADD_PROGRAM_LINE:
        return 'Add Program Line';
      case ESidePanelView.EDIT_PROGRAM_LINE:
        return 'Edit Program Line';
      case ESidePanelView.COPY_PROGRAM_LINE:
        return 'Add Program Line';

      // NEW: e.g. "ADD_SOIL_DATA" or "EDIT_SOIL_DATA"
      case ESidePanelView.ADD_SOIL_DATA:
        return 'Soil Data';
      case ESidePanelView.ADD_CUSTOMER_CONTRACT:
        return 'Create New Contract';
      case ESidePanelView.ADD_FD_USER:
        return 'Add FdUser';
      case ESidePanelView.EDIT_FD_USER: {
        const fdUser = sidePanelData as Partial<IFdUser>;
        return `Edit ${fdUser.name} ${fdUser.surname}`;
      }
      case ESidePanelView.ADD_FD_FARM:
        return 'Add Farm';
      case ESidePanelView.EDIT_FD_FARM: {
        const farm = sidePanelData as Partial<IFdFarm>;
        return `Edit ${farm.name}`;
      }
      case ESidePanelView.ADD_FD_COMPANY:
        return 'Add Company';
      case ESidePanelView.EDIT_FD_COMPANY: {
        const company = sidePanelData as Partial<ISupplierCompanyProfile>;
        return `Edit ${company.name}`;
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_PLANS: {
        return `Edit Production Plans`;
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_PLAN: {
        const {plan} = sidePanelData as ProductionPlanSidePanelData;
        return !plan
          ? `Add Production Plan`
          : `Edit ${plan.crop?.name} Production Plan`;
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_HISTORY: {
        const {cropName} = sidePanelData as ProductionHistorySidePanelData;
        return `Edit ${cropName} Production History`;
      }
      case ESidePanelView.EDIT_FD_FINANCIAL_HISTORY:
        return 'Edit Financial History';
      case ESidePanelView.EDIT_FD_OPERATING_COSTS:
        return 'Edit Operating Costs';
      case ESidePanelView.EDIT_FD_OPERATING_COST:
        const {operatingCost} = sidePanelData as OperatingCostSidePanelData;
        if (!operatingCost?.expense?.name) return 'Add Operating Cost';
        else return `Edit ${operatingCost.expense.name}`;
      case ESidePanelView.EDIT_FD_ASSETS:
        return 'Edit Assets';
      case ESidePanelView.EDIT_FD_ASSET:
        const {asset} = sidePanelData as AssetSidePanelData;
        if (!asset?.description) return 'Add Asset';
        else return `Edit ${asset.description}`;
      case ESidePanelView.EDIT_FD_LIABILITIES:
        return 'Edit Liabilities';
      case ESidePanelView.EDIT_FD_LIABILITY:
        const {liability} = sidePanelData as LiabilitySidePanelData;
        if (!liability?.liabilityAccountType?.name) return 'Add Liability';
        else return `Edit ${liability.liabilityAccountType?.name}`;
      case ESidePanelView.EDIT_FD_PRODUCTION_COST:
        const {crop} = sidePanelData as ProductionCostSidePanelData;
        return `Edit ${crop?.name} Production Costs`;

      case ESidePanelView.ADD_WALLET_RESTRICTION:
        return 'Add Wallet Restriction';
      case ESidePanelView.EDIT_WALLET_RESTRICTION:
        return 'Edit Wallet Restriction';

      case ESidePanelView.EDIT_FD_FARM_TEAM:
        return 'Edit Team';

      case ESidePanelView.EDIT_FD_CUSTOMERS:
        return 'Primary Customers';

      case ESidePanelView.EDIT_FD_SUPPLIERS:
        return 'Primary Suppliers';

      case ESidePanelView.EDIT_FD_RISKS:
        return 'Top Risks';

      case ESidePanelView.EDIT_FD_FUNDING_ASK:
        return 'Edit Funding Ask';

      default:
        return null;
    }
  }

  // Render the correct panel content
  function panelContent() {
    switch (panelType) {
      case ESidePanelView.INVOICE:
        return <Invoice data={sidePanelData} />;
      case ESidePanelView.ADD_TEAM_MEMBER:
        return (
          <AddOrUpdateTeamMember
            loggedInUser={props.user}
            depots={props.depotState.depots}
          />
        );
      case ESidePanelView.EDIT_TEAM_MEMBER:
        return (
          <AddOrUpdateTeamMember
            loggedInUser={props.user}
            user={sidePanelData}
            depots={props.depotState.depots}
          />
        );
      case ESidePanelView.EDIT_TEAM_MEMBER_PERMISSIONS:
        return (
          <EditTeamMemberPermissions
            loggedInUser={props.user}
            userId={sidePanelData?.userId}
          />
        );
      case ESidePanelView.REMOVE_TEAM_MEMBER:
        return <RemoveTeamMember teamMember={sidePanelData} />;
      case ESidePanelView.CREATE_QUOTE:
        return <AddQuote user={props.user} productState={props.productState} />;
      case ESidePanelView.ADD_CUSTOMER:
        return <AddCustomer />;
      case ESidePanelView.ADD_CUSTOMER_CONTRACT:
        return <AddContracts data={sidePanelData} />;
      case ESidePanelView.ADD_DEPOT:
        return <AddOrUpdateDepot user={props.user} />;
      case ESidePanelView.EDIT_DEPOT:
        return <AddOrUpdateDepot user={props.user} depot={sidePanelData} />;
      case ESidePanelView.ADD_PRODUCT:
        return (
          <AddOrUpdateProduct
            user={props.user}
            allDepots={depots}
            allProducts={props.productState.products}
          />
        );
      case ESidePanelView.EDIT_PRODUCT:
        return (
          <AddOrUpdateProduct
            user={props.user}
            allDepots={depots}
            allProducts={props.productState.products}
            product={sidePanelData}
          />
        );
      case ESidePanelView.COPY_PRODUCT:
        return (
          <AddOrUpdateProduct
            user={props.user}
            allDepots={depots}
            allProducts={props.productState.products}
            product={sidePanelData}
            isCopy
          />
        );
      case ESidePanelView.PROMOTE_PRODUCT:
        return (
          <PromoteProduct
            user={props.user}
            depots={depots}
            product={sidePanelData}
          />
        );
      case ESidePanelView.EDIT_INVENTORY: {
        const productData = sidePanelData as InventorySidePanelData;
        return (
          <AddOrUpdateInventory
            user={props.user}
            allDepots={depots}
            product={productData.product}
            depotIds={productData.depotIds}
          />
        );
      }
      case ESidePanelView.ADD_PROGRAM:
        return <AddOrUpdateProgram user={props.user} program={sidePanelData} />;
      case ESidePanelView.ADD_PROGRAM_LINE:
        return (
          <AddOrUpdateProgramLine
            user={props.user}
            programLine={sidePanelData}
            allProducts={props.productState.products}
          />
        );
      case ESidePanelView.EDIT_PROGRAM_LINE:
        return (
          <AddOrUpdateProgramLine
            user={props.user}
            programLine={sidePanelData.data}
            index={sidePanelData.index}
            allProducts={props.productState.products}
          />
        );
      case ESidePanelView.COPY_PROGRAM_LINE:
        return (
          <AddOrUpdateProgramLine
            user={props.user}
            programLine={sidePanelData}
            allProducts={props.productState.products}
          />
        );

      case ESidePanelView.ADD_SOIL_DATA:
        return (
          <AddOrUpdateSoilData
            soilData={sidePanelData?.data || sidePanelData}
            readOnly={panelInfo?.readOnly}
          />
        );
      case ESidePanelView.ADD_FD_USER:
        return <AddOrUpdateFdUser user={props.user} />;
      case ESidePanelView.EDIT_FD_USER:
        return <AddOrUpdateFdUser user={props.user} fdUser={sidePanelData} />;
      case ESidePanelView.ADD_FD_FARM:
        return <AddOrUpdateFdFarm user={props.user} />;
      case ESidePanelView.EDIT_FD_FARM:
        return <AddOrUpdateFdFarm user={props.user} farm={sidePanelData} />;
      case ESidePanelView.ADD_FD_COMPANY:
        return <AddOrUpdateFdCompany user={props.user} />;
      case ESidePanelView.EDIT_FD_COMPANY:
        return (
          <AddOrUpdateFdCompany user={props.user} company={sidePanelData} />
        );
      case ESidePanelView.EDIT_FD_PRODUCTION_PLANS: {
        const {applicationId, farmId, plans, history} =
          sidePanelData as ProductionPlanAndHistorySidePanelData;
        return (
          <EditFdProductionPlans
            user={props.user}
            applicationId={applicationId}
            farmId={farmId}
            plans={plans}
            history={history}
          />
        );
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_PLAN: {
        const {applicationId, plan} =
          sidePanelData as ProductionPlanSidePanelData;
        return (
          <AddOrUpdateFdProductionPlan
            user={props.user}
            applicationId={applicationId}
            plan={plan}
          />
        );
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_HISTORY: {
        const {applicationId, farmId, history, cropId} =
          sidePanelData as ProductionHistorySidePanelData;
        return (
          <AddOrUpdateFdProductionHistory
            user={props.user}
            applicationId={applicationId}
            farmId={farmId}
            cropId={cropId}
            allHistory={history}
          />
        );
      }
      case ESidePanelView.EDIT_FD_FINANCIAL_HISTORY: {
        const {applicationId, companyId, history, application} =
          sidePanelData as FinancialHistorySidePanelData;
        if (!application) {
          return null;
        }
        return (
          <AddOrUpdateFdFinancialHistory
            user={props.user}
            allHistory={history}
            applicationId={applicationId}
            companyId={companyId}
            application={application as IFdApplicationType}
          />
        );
      }
      case ESidePanelView.EDIT_FD_OPERATING_COSTS: {
        const {applicationId, operatingCosts} =
          sidePanelData as OperatingCostsSidePanelData;
        return (
          <EditFdOperatingCosts
            user={props.user}
            applicationId={applicationId}
            operatingCosts={operatingCosts}
          />
        );
      }
      case ESidePanelView.EDIT_FD_OPERATING_COST: {
        const {applicationId, operatingCost} =
          sidePanelData as OperatingCostSidePanelData;
        return (
          <AddOrUpdateFdOperatingCost
            user={props.user}
            applicationId={applicationId}
            operatingCost={operatingCost}
          />
        );
      }
      case ESidePanelView.EDIT_FD_ASSETS: {
        const {applicationId, farmId, assets, onAssetUpserted} =
          sidePanelData as AssetsSidePanelData;
        return (
          <EditFdAssets
            user={props.user}
            applicationId={applicationId}
            farmId={farmId}
            assets={assets}
            onAssetUpserted={onAssetUpserted}
          />
        );
      }
      case ESidePanelView.EDIT_FD_ASSET: {
        const {applicationId, farmId, asset, onAssetUpserted} =
          sidePanelData as AssetSidePanelData;
        return (
          <AddOrUpdateFdAsset
            user={props.user}
            applicationId={applicationId}
            farmId={farmId}
            asset={asset}
            onAssetUpserted={onAssetUpserted}
          />
        );
      }
      case ESidePanelView.EDIT_FD_LIABILITIES: {
        const {applicationId, liabilities} =
          sidePanelData as LiabilitiesSidePanelData;
        return (
          <EditFdLiabilities
            user={props.user}
            applicationId={applicationId}
            liabilities={liabilities}
          />
        );
      }
      case ESidePanelView.EDIT_FD_LIABILITY: {
        const {applicationId, liability} =
          sidePanelData as LiabilitySidePanelData;
        return (
          <AddOrUpdateFdLiability
            user={props.user}
            applicationId={applicationId}
            liability={liability}
          />
        );
      }
      case ESidePanelView.EDIT_FD_PRODUCTION_COST: {
        const {applicationId, productionCost, crop} =
          sidePanelData as ProductionCostSidePanelData;
        return (
          <AddOrUpdateFdProductionCost
            user={props.user}
            applicationId={applicationId}
            productionCost={productionCost}
            cropId={parseInt(crop.id)}
          />
        );
      }

      case ESidePanelView.EDIT_FD_FARM_TEAM:
        return (
          <AddOrUpdateFdTeam
            user={props.user}
            applicationId={sidePanelData.applicationId}
            initialTeam={sidePanelData.team}
          />
        );

      case ESidePanelView.EDIT_FD_CUSTOMERS: {
        const {applicationId, customerSuppliers} =
          sidePanelData as CustomerSupplierSidePanelData;

        return (
          <AddOrUpdateFdCustomers
            user={props.user}
            applicationId={applicationId}
            initialLinks={customerSuppliers}
          />
        );
      }

      case ESidePanelView.EDIT_FD_SUPPLIERS: {
        const {applicationId, farmerSuppliers} =
          sidePanelData as FarmerSupplierSidePanelData;
        return (
          <AddOrUpdateFdSuppliers
            user={props.user}
            applicationId={applicationId}
            initialLinks={farmerSuppliers}
          />
        );
      }

      case ESidePanelView.EDIT_FD_RISKS: {
        const {applicationId} = sidePanelData as RisksSidePanelData;
        return <AddOrUpdateFdRisks applicationId={applicationId} />;
      }

      case ESidePanelView.EDIT_FD_FUNDING_ASK: {
        const {application, applicationId} = sidePanelData as {
          application: IFdApplicationType;
          applicationId: number;
        };

        return (
          <AddOrUpdateFdFundingAsk
            application={application}
            applicationId={applicationId}
          />
        );
      }

      case ESidePanelView.ADD_WALLET_RESTRICTION:
        return (
          <AddOrUpdateWalletRestrictions
            walletId={sidePanelData?.walletId}
            walletBalance={sidePanelData?.walletBalance}
            existingRestrictions={sidePanelData?.existingRestrictions}
            restriction={sidePanelData?.restriction}
            onRestrictionAdded={sidePanelData?.onRestrictionAdded}
          />
        );
      case ESidePanelView.EDIT_WALLET_RESTRICTION:
        return (
          <AddOrUpdateWalletRestrictions
            walletId={sidePanelData?.walletId}
            walletBalance={sidePanelData?.walletBalance}
            existingRestrictions={sidePanelData?.existingRestrictions}
            restriction={sidePanelData?.restriction}
            onRestrictionAdded={sidePanelData?.onRestrictionAdded}
          />
        );

      case ESidePanelView.CUSTOMISE_OVERVIEW:
        return <CustomizeOverviewPanel />;

      default:
        return null;
    }
  }

  return (
    <div className={`sidepanel-container ${isOpen ? 'open' : ''}`}>
      {/* Overlay background, click to close */}
      <div
        onClick={closePanel}
        className={`sidepanel-opacity ${opacityVisible ? 'open' : 'closed'}`}
      />

      {/* The side panel */}
      <div
        className={`sidepanel-content ${isOpen ? 'open' : ''} ${
          isWide ? 'wide' : ''
        }`}
      >
        {/* Close Button */}
        <IconButton
          className="close-ic"
          onClick={closePanel}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>

        <div className="separator"></div>

        {/* Panel Title */}
        <div className="sidepanel-header">
          <Typography variant="h6" noWrap>
            {panelTitle()}
          </Typography>
        </div>

        <div className="side-panel-view">{panelContent()}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    sidepanel: state.sidepanel,
    user: state.user,
    productState: state.products,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(SidePanel);
